import React, { useState, useLayoutEffect, useEffect } from 'react';
import axios from 'axios';

import { EMPLOYEE_DASHBOARD } from '../../../data/StateValues';

import DashboardWelcome from '../User/DashboardWelcome';
import EmployeeNavbar from './GeneralDisplays/EmployeeNavbar';
import ApplicationControl from './ApplicationControlDisplays/ApplicationControl';
import AffiliateControl from './ApplicationControlDisplays/AffiliateDisplays/AffiliateControl';
import EventControl from './EventControlDisplays/EventControl';
import EmployeeReportControl from './ReportControlDisplays/ReportControl';
import StatisticsControl from './StatisticsControlDisplays/StatisticsControl';
import SettingsControl from './SettingsControlDisplays/SettingsControl';
import AccessDenied from './GeneralDisplays/AccessDenied';
import LoadingSection from './GeneralDisplays/LoadingSection';
import NoSelection from './GeneralDisplays/NoSelection';

import Fade from 'react-reveal/Fade';

import "./EmployeeDashboardStyles.css";


const applicationAccessKey = 'Application Access';
const eventAccessKey = 'Event Access';
const userAccessKey = 'User Access';
const reportAccessKey = 'Report Access';

function EmployeeDashboard(props) {
    const [display, setDisplay] = useState(EMPLOYEE_DASHBOARD.applications);
    const [legacyApplications, setLegacyApplications] = useState(null);
    const [legacyApplicationsLoading, setLegacyApplicationsLoading] = useState(true);
    const [applications, setApplications] = useState(null);
    const [pendingApplications, setPendingApplications] = useState(null);
    const [affiliates, setAffiliates] = useState(null);
    const [affiliatesLoading, setAffiliatesLoading] = useState(true);
    const [events, setEvents] = useState(null);
    const [upcomingEvents, setUpcomingEvents] = useState(null);
    const [reports, setReports] = useState(null);
    const [pendingReports, setPendingReports] = useState(null);
    const [users, setUsers] = useState(null);

    // Loading States
    const [applicationsLoading, setApplicationsLoading] = useState(true);
    const [eventsLoading, setEventsLoading] = useState(true);
    const [reportsLoading, setReportsLoading] = useState(true);
    const [usersLoading, setUsersLoading] = useState(true);

    // Access States
    const [applicationAccess, setApplicationAccess] = useState(null);
    const [eventAccess, setEventAccess] = useState(null);
    const [reportAccess, setReportAccess] = useState(null);
    const [userAccess, setUserAccess] = useState(null);

    useEffect(() => {
        axios.defaults.withCredentials = true;

        // Applications

        if (props.userData.clientSidePermissions.includes(applicationAccessKey)) {
            setApplicationAccess(true);
            if (!applications) {
                axios
                    .get('/api/applications', { withCredentials: true })
                    .then((res) => {
                        // Removed sort by date function, .reverse() works way easier
                        setApplications(res.data.applications.reverse());
                        // Parse applications and get total pending
                        var localPending = 0;
                        res.data.applications.forEach(application => {
                            if (application.status === "Pending") {
                                localPending++;
                            }
                        });

                        setPendingApplications(localPending);
                        setApplicationsLoading(false);
                    })
                    .catch((err) => {
                        setApplicationsLoading(false);
                    });
            }

            // Legacy Applications
            if (!legacyApplications) {
                setLegacyApplicationsLoading(true);
                axios
                    .get('/api/legacy-applications', { withCredentials: true })
                    .then((res) => {
                        setLegacyApplications(res.data.applications.reverse());
                        setLegacyApplicationsLoading(false);
                    })
                    .catch((err) => {
                        setApplicationsLoading(false);
                    });
            }

            // Affiliate Applications
            if (!affiliates) {
                setAffiliatesLoading(true);
                axios
                    .get('/api/affiliates', { withCredentials: true })
                    .then((res) => {
                        setAffiliates(res.data.affiliates.reverse());
                        setAffiliatesLoading(false);
                    })
                    .catch((err) => {
                        setAffiliatesLoading(false);
                    });
            }
        } else {
            setApplicationAccess(false);
            setApplicationsLoading(false);
        }

        // Events
        if (props.userData.clientSidePermissions.includes(eventAccessKey)) {
            setEventAccess(true);
            if (!events) {
                setEventsLoading(true);
                axios.get('/api/events', { withCredentials: true })
                    .then((res) => {
                        setEvents(res.data.events);
                        // Parse events and get total upcoming
                        var localUpcoming = 0;
                        res.data.events.forEach(event => {
                            if (event.active) {
                                localUpcoming++;
                            }
                        })
                        setUpcomingEvents(localUpcoming);
                        setEventsLoading(false);
                    })
                    .catch((err) => {
                        setEventsLoading(false);
                    })
            }
        } else {
            setEventAccess(false);
            setEventsLoading(false);
        }


        // Reports
        if (props.userData.clientSidePermissions.includes(reportAccessKey)) {
            setReportAccess(true);
            if (!reports) {
                setReportsLoading(true);
                axios.get('/api/reports', { withCredentials: true })
                    .then((res) => {
                        setReports(res.data.reports.reverse());
                        // Parse reports and get total unresolved
                        var localPending = 0;
                        res.data.reports.forEach(report => {
                            if (!report.resolved) {
                                localPending++;
                            }
                        })
                        setPendingReports(localPending);
                        setReportsLoading(false);
                    })
                    .catch((err) => {
                        setReportsLoading(false);
                    })
            }
        } else {
            setReportAccess(false);
            setReportsLoading(false);
        }

        // Users
        if (props.userData.clientSidePermissions.includes(userAccessKey)) {
            setUserAccess(true);
            if (!users) {
                setUsersLoading(true);
                axios.get('/api/users', { withCredentials: true })
                    .then((res) => {
                        setUsers(res.data.users);
                        setUsersLoading(false);
                    })
                    .catch((err) => {
                        setUsersLoading(false);
                    })
            }
        } else {
            setUserAccess(false);
            setUsersLoading(false);
        }
    }, [props.userData.clientSidePermissions])

    const updateDisplay = (value) => {
        setDisplay(value);
    }

    const renderDisplayContainer = () => {
        switch (display) {
            case EMPLOYEE_DASHBOARD.nothing:
                return (
                    <div className='display-container'>
                        <h1 className='header'>Nothing Here Yet!</h1>
                        <p>Press a button above to get started.</p>
                    </div>
                )

            case EMPLOYEE_DASHBOARD.applications: {
                // Check for loading
                if (!applicationsLoading && !legacyApplicationsLoading) {
                    // Check for access
                    if (applicationAccess) {
                        return (
                            <ApplicationControl applications={applications} setApplications={setApplications}
                                legacyApplications={legacyApplications} setLegacyApplications={setLegacyApplications}
                            />
                        )
                    } else {
                        return (
                            <AccessDenied sectionHeader={'Applications'} />
                        )
                    }
                } else {
                    return (
                        <>
                            <LoadingSection sectionHeader={'Applications'} />
                        </>
                    )
                }
            }

            case EMPLOYEE_DASHBOARD.affiliateApplications: {
                // Check for loading
                if (!affiliatesLoading) {
                    // Check for access
                    if (applicationAccess) {
                        return (
                            <>
                                <AffiliateControl affiliates={affiliates} setAffiliates={setAffiliates} applications={applications} />
                            </>
                        )
                    } else {
                        return (
                            <AccessDenied sectionHeader={'Applications'} />
                        )
                    }
                } else {
                    return (
                        <>
                            <LoadingSection sectionHeader={'Applications'} />
                        </>
                    )
                }
            }

            case EMPLOYEE_DASHBOARD.reports: {
                // Check for loading
                if (!reportsLoading) {
                    // Check for access
                    if (reportAccess) {
                        return (
                            <EmployeeReportControl reports={reports} setReports={setReports} />
                        )
                    } else {
                        return (
                            <>
                                <AccessDenied sectionHeader={'Reports'} />
                            </>
                        )
                    }

                } else {
                    return (
                        <LoadingSection sectionHeader={'Reports'} />
                    )
                }
            }

            case EMPLOYEE_DASHBOARD.events: {
                // Check for loading
                if (!eventsLoading) {
                    // Check for access
                    if (eventAccess) {
                        return (
                            <EventControl events={events} setEvents={setEvents} upcomingEvents={upcomingEvents} />
                        )
                    } else {
                        return (
                            <>
                                <AccessDenied sectionHeader={'Events'} />
                            </>
                        )
                    }

                } else {
                    return (
                        <LoadingSection sectionHeader={'Events'} />
                    )
                }
            }

            case EMPLOYEE_DASHBOARD.stats: {
                // Check for Loading
                if (!usersLoading && !eventsLoading && !reportsLoading && !applicationsLoading) {
                    // Check for Access
                    if (userAccess, eventAccess, reportAccess, applicationAccess) {
                        return (
                            <StatisticsControl userData={props.userData}
                                applications={applications}
                                users={users}
                                events={events}
                                reports={reports}
                                legacyApplications={legacyApplications} />
                        )
                    } else {
                        return (
                            <>
                                <AccessDenied sectionHeader={'Statistics'} />
                            </>
                        )
                    }
                }
            }

            case EMPLOYEE_DASHBOARD.settings: {
                // Check for loading
                if (!usersLoading && !eventsLoading && !reportsLoading && !applicationsLoading) {
                    // Check for Access
                    if (userAccess, eventAccess, reportAccess, applicationAccess) {
                        return (
                            <SettingsControl users={users} setUsers={setUsers}
                                applications={applications}
                                events={events} reports={reports} legacyApplications={legacyApplications} userData={props.userData}
                            />
                        )
                    } else {
                        return (
                            <>
                                <AccessDenied sectionHeader={'Settings'} />
                            </>
                        )
                    }

                } else {
                    return (
                        <LoadingSection sectionHeader={'Settings'} />
                    )
                }
            }

            default:
                return (
                    <NoSelection />
                )
        }
    }

    return (
        <>
            <Fade>
                <DashboardWelcome fName={props.userData.firstName} />
                <EmployeeNavbar applications={applicationsLoading ? '...' : pendingApplications}
                    reports={reportsLoading ? '...' : pendingReports}
                    events={eventsLoading ? '...' : upcomingEvents}
                    updateDisplay={updateDisplay}
                    displaying={display}
                />
            </Fade>

            {renderDisplayContainer()}

        </>
    )
}

export default EmployeeDashboard;